import React from "react";
import {
  ISchoolStudentApplicationCardBackEndModel,
  ISchoolStudentApplicationCardFrontEndModel,
  ISchoolStudentApplicationCardHeaderContent,
  IStudentApplicationCardCenterContent,
} from "../types";
import { PROGRAM_TYPES, ProgramType } from "../../program/constants";
import {
  beautifyRequiredDocuments,
  calcFeeWithScholarshipAndFinancialAid,
  sumTuitionAndOtherFeesSupports,
} from "../../program/helpers/helperFunctions";
import moment from "moment";
import { beautifyDurationUnit } from "../../../helpers";
import { EducationType } from "../../school/constants/types";
import { ColorName } from "../../../types";
import { ReactComponent as SubmissionDate } from "../../../assets/svg/hourglass.svg";
import { ReactComponent as StartDate } from "../../../assets/svg/calendar-yellow.svg";
import { ReactComponent as Deadline } from "../../../assets/svg/calendar-red.svg";
import { CardFieldType } from "../../program/components/constants";
import { ReactComponent as RequiredDocumentsInsideCircle } from "../../../assets/svg/required-documents-inside-circle.svg";
import { ExpandedRequiredDocuments } from "../../program/components/CardField/components/ExpandedRequiredDocuments";
import { ReactComponent as Branch } from "../../../assets/svg/branch-location.svg";
import { ReactComponent as ProgramDuration } from "../../../assets/svg/duration.svg";

function parser(
  application: ISchoolStudentApplicationCardBackEndModel,
  schoolType: EducationType,
) {
  const capitalizedRequiredDocuments = beautifyRequiredDocuments(
    application.program.academic.requiredDocs,
  );

  const { hasSupport: hasTuitionSupport, calcFee: calcTuition } =
    calcFeeWithScholarshipAndFinancialAid(
      application.program.tuition,
      application.program.hasTuitionScholarship,
      application.program.tuitionScholarshipType,
      application.program.tuitionScholarshipAmount,
      application.program.hasTuitionFinancialAid,
      application.program.tuitionFinancialAidType,
      application.program.tuitionFinancialAidAmount,
      application.program.currency,
    );

  const { calcFee: calcOtherFees, hasSupport: hasOtherFeesSupport } =
    calcFeeWithScholarshipAndFinancialAid(
      application.program.otherFeesTotal,
      application.program.hasOtherFeesScholarship,
      application.program.otherFeesScholarshipType,
      application.program.otherFeesScholarshipAmount,
      application.program.hasOtherFeesFinancialAid,
      application.program.otherFeesFinancialAidType,
      application.program.otherFeesFinancialAidAmount,
      application.program.currency,
    );

  const {
    tuitionOtherFeesSumWithCurrency,
    tuitionOtherFeesWithSupportsSumWithCurrency,
  } = sumTuitionAndOtherFeesSupports(
    application.program.tuition,
    hasTuitionSupport,
    calcTuition,
    application.program.otherFeesTotal,
    hasOtherFeesSupport,
    calcOtherFees,
    application.program.currency,
  );

  const programDuration = `${
    application.program.durationAmount
  } ${beautifyDurationUnit(
    application.program.durationAmount,
    application.program.durationUnit,
    "Program",
  )}`;

  const tuitionDuration = `${
    application.program.tuitionDurationAmount == null
      ? ""
      : application.program.tuitionDurationAmount + " "
  }
        ${beautifyDurationUnit(
          application.program.tuitionDurationAmount,
          application.program.tuitionDurationUnit,
          "Program",
        )}`;

  const totalTuitionWithUnit =
    tuitionOtherFeesWithSupportsSumWithCurrency + " / " + tuitionDuration;

  const schoolCancellationNote = !application.schoolCancellationNote
    ? null
    : {
        ...application.schoolCancellationNote,
        date: moment(application.schoolCancellationNote.created_at).format(
          "ddd, MMM D, YYYY, h:mm A",
        ),
      };

  const studentCancellationNote = !application.studentCancellationNote
    ? null
    : {
        ...application.studentCancellationNote,
        date: moment(application.studentCancellationNote.created_at).format(
          "ddd, MMM D, YYYY, h:mm A",
        ),
      };

  const headerContent: ISchoolStudentApplicationCardHeaderContent = {
    leftSection: {
      lRow0: {
        text0:
          application.program.majorName ??
          application.program.academic.levelName!,
        text1: application.program.academic.grade,
      },
      lRow2: {
        text0: application.program.academic.degreeName ?? "School Type - ",
        text1: application.program.academic.schoolTypeName,
      },
    },
    rightSection: {
      rRow0: {
        text0: PROGRAM_TYPES.find(
          (type) => type.id === application.program.programType,
        )!.name,
        text0BackgroundColor:
          application.program.programType === +ProgramType.ONLINE
            ? ColorName.ONLINE
            : ColorName.ON_CAMPUS,
      },
      rRow1: {
        text0: totalTuitionWithUnit,
        text1: tuitionOtherFeesSumWithCurrency,
      },
    },
  };

  const centerContent: IStudentApplicationCardCenterContent[] = [
    {
      id: CardFieldType.SubmissionDate,
      icon: SubmissionDate,
      title: "Submission Date",
      previewContent: [application.created_at],
    },
    {
      id: CardFieldType.StartDate,
      icon: StartDate,
      title: "Start Date",
      previewContent: [application.intake.startDate],
    },
    {
      id: CardFieldType.Deadline,
      icon: Deadline,
      title: "Deadline",
      previewContent: [application.intake.deadline],
    },
    {
      id: CardFieldType.RequiredDocuments,
      icon: RequiredDocumentsInsideCircle,
      title: "Required Documents",
      previewContent: capitalizedRequiredDocuments,
      expandedContent: () => (
        <ExpandedRequiredDocuments reqDocs={capitalizedRequiredDocuments} />
      ),
    },
    {
      id: CardFieldType.Branches,
      icon: Branch,
      title: "Branches",
      previewContent: [application.branch.name],
    },
    {
      id: CardFieldType.ProgramDuration,
      icon: ProgramDuration,
      title: "Program Duration",
      previewContent: [programDuration],
    },
  ];

  const parsedApplication: ISchoolStudentApplicationCardFrontEndModel = {
    headerContent,
    centerContent,
    schoolType,

    schoolCancellationNote,
    studentCancellationNote,
    id: application.id,
    status: application.status,
    startDate: application.intake.startDate,
    submissionDate: application.created_at,
    entityId: application.program.id,
    entityName: "program",
  };

  return parsedApplication;
}

export function parseToSchoolStudentCardsProgramFrontEndModel(
  response,
  schoolType,
) {
  response.data = response.data?.map((program) => parser(program, schoolType));

  return response;
}
